import React, { ReactNode, useState } from 'react';
import { ArrowDownward, ArrowUpward, ExpandMore, ImportExport } from '@mui/icons-material';
import {
    DropdownMenu, DropdownToggle, Input, Label, UncontrolledDropdown
} from 'reactstrap';
import { Grid2, GridSize, Hidden } from "@mui/material";
import { Breakpoint } from "@mui/material/styles";
import { getUserExistingRulebookMode } from '../../shared/store/applicationState';
import { RulebookModeEnum, RulebookNameToEnum } from '../../shared/enums/rulebook-mode-enum';

export const MobileBreakpoints = ["xs", "sm", "md"] as Breakpoint[];
export const DesktopBreakpoints = ["lg", "xl"] as Breakpoint[];

interface IPropsWithGridSizes { xs?: GridSize, sm?: GridSize, md?: GridSize, lg?: GridSize, xl?: GridSize };
interface IGridColProps extends IPropsWithGridSizes {
    hiddenBreakpoints?: Breakpoint | Breakpoint[],
    className?: string,
    children?: ReactNode,
    onClick?: () => void
};

export const GridCol = (props: IGridColProps) => {
    return (
        <Hidden only={props.hiddenBreakpoints}>
            <Grid2 size={{xs: props.xs, sm: props.sm, md: props.md, lg: props.lg, xl: props.xl}}
                className={props.className}
                onClick={props.onClick}>
                {props.children}
            </Grid2>
        </Hidden>
    );
};

export interface ISortableGridColProps extends IGridColProps {
    fieldName: string,
    sortStyle: SortStyle,
    sortFn: (field: string, style: SortStyle) => void,
    sortConfig: ISortConfig | null
}

export const SortableGridCol = (props: ISortableGridColProps) => {
    const colProps = props as IGridColProps;
    return (
        <GridCol {...colProps} onClick={() => { props.sortFn(props.fieldName, props.sortStyle); }}>
            {props.children} <SortArrow fieldName={props.fieldName} config={props.sortConfig ?? {} as ISortConfig} />
        </GridCol>
    );
};

export const GridLabel = (props: { title: string, text: string }) => (
    <div className="grid-label">
        <span className="cell-label-text">
            {props.title}
        </span>
        <span className="cell-text">
            {props.text}
        </span>
    </div>
);

export const GridRulebookLabel = (props: { cbtc: boolean | undefined, traditional: boolean | undefined }) => {
    const mode = getUserExistingRulebookMode();
    const cbtc = props.cbtc && (RulebookNameToEnum(mode) === RulebookModeEnum.CBTC);
    const traditional = props.traditional && (RulebookNameToEnum(mode) === RulebookModeEnum.Traditional);
    return (
        <span className={cbtc ? "doc-rulebook-label mbs" : traditional ? "doc-rulebook-label traditional" : 'doc-rulebook-label traditional'}>
            {cbtc ? 'MBS NETWORK' : traditional ? 'RAIL NETWORK' : 'RAIL NETWORK'}
        </span>
    )
};

export const SortArrow = (props: { fieldName: string, config: ISortConfig | null }) => {
    if (props.config && props.fieldName === props.config.fieldName && props.config.direction === SortOrder.DESC) {
        return (
            <ArrowUpward />
        );
    }
    else if (props.config && props.fieldName === props.config.fieldName && props.config.direction === SortOrder.ASC) {
        return (
            <ArrowDownward />
        );
    }

    return (
        <ImportExport />
    );
}

export const FilterArrow = (props: { text: string, filterItems: Array<string>, onFilter: (items: Array<string>) => void }) => {
    const [selectedItems, setSelectedItems] = useState([] as Array<string>);

    const onToggle = (isOpen: boolean) => {
        if (isOpen) {
            // if w're closeing, then apply the filter
            props.onFilter(selectedItems);
        }
    };

    const filterItemCheck = (i: string): boolean => {
        return selectedItems.filter((item) => item === i).length > 0;
    };

    const filterItemChangeAll = () => {
        let arr: string[] = [];

        if (!filterItemCheck('All')) { //check all
            arr.push('All');
            props.filterItems.map(x => {
                arr.push(x);
            });
        }

        setSelectedItems(arr);
    };

    const toggleFilterItem = (itemtoToggle: string) => {
        const arr = selectedItems.map(x => x);

        const idx = arr.findIndex(x => x === itemtoToggle);

        if (idx > -1)
            arr.splice(idx, 1);
        else
            arr.push(itemtoToggle);

        setSelectedItems(arr);
    };

    const doNothing = () => { };

    return (
        <UncontrolledDropdown setActiveFromChild onToggle={(e, o) => { onToggle(o) }}>
            <DropdownToggle tag='div' className={'dropdown-link' + (selectedItems.length > 0 ? " filtered" : " unfiltered")} caret>
                {props.text}  {props.filterItems.length > 1 && <ExpandMore />}
            </DropdownToggle>
            <DropdownMenu>

                {props.filterItems.length > 0 &&
                    <div key={0} className="form-check" onClick={() => { filterItemChangeAll(); }}>
                        <div className="form-check-inline">
                            <Input type="checkbox" className="form-check-input mb-1 ml-2" checked={filterItemCheck('All')} onChange={doNothing} />
                            <Label className="form-check-label" >{'All'}</Label>
                        </div>
                    </div>
                }

                {props.filterItems.map((i, n) => (
                    <span key={n}>
                        <hr className="form-check-hr" />
                        <div className="form-check top-border" onClick={() => toggleFilterItem(i)}>
                            <div className="form-check-inline">
                                <Input type="checkbox" className="form-check-input mb-1 ml-2" checked={filterItemCheck(i)} onChange={doNothing} />
                                <Label className="form-check-label" >{i}</Label>
                            </div>
                        </div>
                    </span>
                ))}

                <div style={{ textAlign: "center", paddingTop: "1rem" }}><button onClick={() => { onToggle(true); }} className="btn btn-primary btn-secondary">Apply</button></div>
            </DropdownMenu>
        </UncontrolledDropdown>
    );
}

export interface ISortConfig {
    fieldName: string,
    direction: SortOrder,
    sortStyle: SortStyle,
}

export type IFilterConfig = {
    filterItems: IFilterItem[]
}

export type IPageConfig = {
    pageSize: number,
    currentPage: number,
    itemCount: number
    hidePageList?: boolean
}

export type IFilterItem = {
    filterName: string,
    filterFn: (element: any, index: number, array: Array<any>) => boolean,
}

type ISortableDataItem<T> = {
    items: Array<T>,
    requestSort: (field: string, style: SortStyle, dir?: SortOrder | null) => void,
    sortConfig: ISortConfig | null,
    requestFilter: (key: string, x?: (element: T, index: number, array: Array<T>) => boolean) => void,
    filterConfig: IFilterConfig | null,
    requestPage: (page: number, pageSize?: number) => void,
    pageConfig: IPageConfig | null;
}

// React hook to sort, filter and page tables
const useSortableData = <T,>(items: Array<T>, sortConfig: ISortConfig | null, filterConfig: IFilterConfig | null, pageConfig: IPageConfig | null): ISortableDataItem<T> => {
    const defaultPageSize = 15;

    // these getters and setters here have different names, not sure if that was intentional but should perhaps be changed for readability?
    const [sortConfigState, setSortConfig] = React.useState(sortConfig);
    const [filterConfigState, setFilterConfig] = React.useState(filterConfig);
    const [pageConfigState, setPageConfig] = React.useState((pageConfig || { pageSize: defaultPageSize, currentPage: 0, itemCount: items.length }) as IPageConfig);

    const sortedAndFilteredItems = React.useMemo(() => {
        let sortableItems = [...items];

        if (filterConfigState && filterConfigState.filterItems) {
            //console.log('filtering items');
            filterConfigState.filterItems.forEach(i => {
                sortableItems = sortableItems.filter(i.filterFn);
            });
        }

        var table = new DataTable(sortableItems);

        pageConfigState.itemCount = sortableItems.length;

        if (sortConfigState) {
            //console.log('sort requested ' + sortConfigState.fieldName + ' ' + sortConfigState.direction);
            if (sortConfigState.direction === SortOrder.ASC)
                table.asc(sortConfigState.fieldName, sortConfigState.sortStyle);
            else
                table.desc(sortConfigState.fieldName, sortConfigState.sortStyle);
        };

        //pageSize of -1 is special case to specify no paging is to be used
        //pageSize of 0 isn't really valid and will always return nothing
        if (pageConfigState.pageSize < 1) {
            return table.getAll();
        }

        return table.getFrom(pageConfigState.currentPage * pageConfigState.pageSize, pageConfigState.pageSize)
    },
        [items, sortConfigState, filterConfigState, pageConfigState]);

    const requestSort = (fieldName: string, sortStyle: SortStyle, dir?: SortOrder | null) => {
        let direction = dir ?? SortOrder.ASC;

        if (dir === undefined && sortConfigState && sortConfigState.fieldName === fieldName && sortConfigState.direction === SortOrder.ASC) {
            direction = SortOrder.DESC;
        }

        setSortConfig({ fieldName, direction, sortStyle } as ISortConfig);
    }

    const requestFilter = (key: string, filterFn?: (element: T, index: number, array: Array<T>) => boolean) => {
        let arrFilterItems: Array<IFilterItem> = [];

        if (filterConfigState) {
            let filterConfig = (filterConfigState as IFilterConfig);
            if (filterConfig.filterItems) {
                arrFilterItems = filterConfig.filterItems?.filter(x => x.filterName !== key);
            }
        }

        arrFilterItems.push({ filterName: key, filterFn } as IFilterItem);
        setFilterConfig({ filterItems: arrFilterItems });
    }

    const requestPage = (page: number, pageSize?: number) => {
        setPageConfig(() => ({ currentPage: page, pageSize: pageSize || pageConfig?.pageSize || defaultPageSize, itemCount: items.length }));
    }

    return {
        items: sortedAndFilteredItems,
        requestSort,
        sortConfig: sortConfigState,
        requestFilter,
        filterConfig: filterConfigState,
        requestPage,
        pageConfig: pageConfigState
    };
}

// using default export here as exporting inline was causing react errors
export default useSortableData;


export enum SortStyle {
    String,
    Numeric,
    CaseSensitiveString
}

export enum SortOrder {
    ASC,
    DESC
}

export class SortArgument {

    public key: string | null = null;
    public order: SortOrder = SortOrder.ASC;
    public style: SortStyle = SortStyle.String;

    constructor(key: string | null = null, order: SortOrder = SortOrder.ASC, style: SortStyle = SortStyle.String) {
        this.key = key;
        this.order = order;
        this.style = style;
    }

    private numericCompare(valueA: number, valueB: number): number {
        if (valueA === valueB) {
            return 0;
        }
        return (this.order == SortOrder.ASC) ? valueA - valueB : valueB - valueA;
    }

    private stringCompare(valueA: string, valueB: string): number {
        if (valueA === valueB) {
            return 0;
        }
        return (this.order == SortOrder.ASC) ? ((valueA < valueB) ? -1 : 1) : ((valueA < valueB) ? 1 : -1);
    }

    private eval(path: string, obj: any): any {
        if (!obj) {
            return null;
        }

        const idx = path.indexOf('.');

        if (idx > -1) {
            const propertyName = path.substring(0, idx);

            if (!obj.hasOwnProperty(propertyName)) {
                console.warn("property not found - " + propertyName);
                return propertyName + " not found";
            }

            return (this.eval(path.substring(idx + 1), obj[propertyName]));
        }

        if (!obj.hasOwnProperty(path)) {
            console.warn("property not found - " + path);
        }

        return obj[path];
    };

    public compare(a: any, b: any): number {
        if (a === null && b === null) return 0;
        if (a === null) return -1;
        if (b === null) return 1;

        // Get values to compare
        if (this.key && (this.key.indexOf(".") > -1)) {
            a = this.eval(this.key, a);
            b = this.eval(this.key, b);
        } else {
            a = (DataTable.isObject(a) && this.key ? a[this.key] : a);
            b = (DataTable.isObject(b) && this.key ? b[this.key] : b);
        }

        if (a === null && b === null) return 0;
        if (a === null) return -1;
        if (b === null) return 1;

        // Numeric sort
        if (this.style == SortStyle.Numeric) {
            return this.numericCompare(
                Number(a),
                Number(b)
            );
        }
        // Case-insensitive string sort
        else if (this.style == SortStyle.String) {
            return this.stringCompare(
                a.toLocaleString().toLocaleLowerCase(),
                b.toLocaleString().toLocaleLowerCase()
            );
        }
        // Case-sensitive string sort
        else if (this.style === SortStyle.CaseSensitiveString) {
            return this.stringCompare(
                a.toLocaleString(),
                b.toLocaleString()
            );
        }
        return 0;
    }

}

export class DataTable {

    private items: Array<any> = [];

    private sortBy: Array<SortArgument> = [];

    constructor(items: Array<any>) {
        this.items = items || [];
    }

    public static isObject(item: any): boolean {
        return (item instanceof Object);
    }

    private sorter(table: DataTable) {
        return (a: any, b: any) => {
            let val: number = 0;
            if (table.sortBy.length > 0) {
                table.sortBy.some(
                    (sortField: SortArgument) => {
                        val = sortField.compare(a, b);
                        // If we got a value then break out
                        return (val != 0);
                    }
                );
            }
            return val || 0;
        }
    };

    private static matches(row: any, key: string, pattern: any) {
        let value = DataTable.isObject(row) ? row[key] : row;
        // Regular expression compare
        if (pattern instanceof RegExp) {
            if (pattern.test(value.toString())) {
                return true;
            }
        }
        // Equality
        else if (value == pattern) {
            return true;
        }
        return false;
    }

    private sort(): DataTable {
        if (this.sortBy.length > 0) {
            this.items.sort(this.sorter(this));
        }
        return this;
    }

    add(row: any): DataTable {
        this.items.push(row);
        return this;
    }

    public remove(index: number): DataTable {
        this.items = this.items.splice(index, 1);
        return this;
    }

    public getSort(): Array<SortArgument> {
        return this.sortBy;
    }

    public clear(): DataTable {
        this.items = [];
        return this;
    }

    public clearSort(): DataTable {
        this.sortBy = [];
        return this;
    }

    public asc(fieldName: string, style: SortStyle = SortStyle.String): DataTable {
        this.sortBy.push(
            new SortArgument(fieldName, SortOrder.ASC, style)
        );
        return this;
    }

    public desc(fieldName: string, style: SortStyle = SortStyle.String): DataTable {
        this.sortBy.push(
            new SortArgument(fieldName, SortOrder.DESC, style)
        );
        return this;
    }

    public getFirst(n: number): Array<any> {
        return this.getAll().slice(0, n);
    }

    public getLast(n: number): Array<any> {
        return this.getAll().slice(n * -1);
    }

    public getFrom(offset: number, limit: number): Array<any> {
        return this.getAll().slice(offset, (offset + limit));
    }

    public getAll(): Array<any> {
        return this.sort().items;
    }

    public filter(key: string, pattern: any): DataTable {
        this.items = this.search(key, pattern);
        return this;
    }

    public search(key: string, pattern: any): Array<any> {
        let matching: Array<any> = [];
        this.sort().items.forEach(row => {
            if (DataTable.matches(row, key, pattern)) {
                matching.push(row);
            }
        });
        return matching;
    }

    public indexOf(key: string, pattern: any): number {
        let index = 0,
            firstMatch: number = -1;
        this.sort().items.some(row => {
            if (DataTable.matches(row, key, pattern)) {
                firstMatch = index;
                return true;
            }
            index++;
            return false;
        });
        return firstMatch;
    }

    public lastIndexOf(key: string, pattern: any): number {
        let index: number = 0,
            lastMatch: number = -1;
        this.sort().items.forEach(row => {
            if (DataTable.matches(row, key, pattern)) {
                lastMatch = index;
            }
            index++;
        });
        return lastMatch;
    }
}